import { RP_HERO_MIN_H, rpJustify } from '../tokens';
import { cn } from '../../lib/cn';
import { RpBullets, RpLinkButton, RpMedia } from '../primitives';
import type { RpSectionOf } from '@mlm/shared';

/**
 * The opening band. Three looks from one config:
 *  - `imageBg` — copy centered over the band's background image (set via
 *    `style.bg = 'image'` + `bgImageKey`; the band and its scrim are drawn by
 *    the section renderer, so the hero only lays out the copy).
 *  - `split`   — copy beside an image or video.
 *  - `plain`   — copy only, on whatever band background is set.
 *
 * `minHeight` is applied to the INNER container rather than the band so the hero
 * needs no special-casing in the renderer: a tall inner box makes a tall band.
 */
export function RpHero({ section }: { section: RpSectionOf<'hero'> }) {
  const c = section.config;
  const minH = RP_HERO_MIN_H[c.minHeight ?? 'md'] ?? RP_HERO_MIN_H.md;

  const copy = (
    <div className="space-y-5">
      {c.eyebrow ? (
        <p className="text-xs font-semibold uppercase tracking-widest opacity-80">{c.eyebrow}</p>
      ) : null}
      <h1 className="font-heading text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl">
        {c.heading}
      </h1>
      {c.subheading ? <p className="text-lg opacity-90">{c.subheading}</p> : null}
      <RpBullets items={c.bullets} className="pt-1" />
      {c.primaryCta || c.secondaryCta ? (
        <div
          className={cn('flex flex-wrap items-center gap-3 pt-2', rpJustify(section.style?.align))}
        >
          {c.primaryCta ? <RpLinkButton button={c.primaryCta} /> : null}
          {c.secondaryCta ? <RpLinkButton button={c.secondaryCta} /> : null}
        </div>
      ) : null}
    </div>
  );

  if (c.variant === 'split') {
    return (
      <div className={cn('grid items-center gap-8 lg:grid-cols-2', minH)}>
        {copy}
        <RpMedia media={c.media} className="w-full" />
      </div>
    );
  }

  // `imageBg` and `plain` share the layout; they differ only in the band styling
  // the admin picks, which the renderer applies.
  return <div className={cn('flex flex-col justify-center', minH)}>{copy}</div>;
}
